import { useEffect, useState } from "react";
import { G } from "@/brand";
import api from "@/lib/api";
import { Music2, Trash2, Plus, ExternalLink } from "lucide-react";

const PLATFORMS = ["Suno", "Udio", "Other"];

export function SavedTracks() {
  const [tracks, setTracks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", platform: "Suno", link: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/tracks").then(({ data }) => setTracks(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tracks", form);
      setTracks((t) => [data, ...t]);
      setForm({ title: "", platform: "Suno", link: "", notes: "" });
      setAdding(false);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/tracks/${id}`);
    setTracks((t) => t.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="saved-tracks" style={{ background: G.dark, border: `1px solid ${G.border}`, padding: 22, marginTop: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div className="mono" style={{ fontSize: 9, letterSpacing: ".3em", color: G.gold, display: "flex", alignItems: "center", gap: 8 }}>
          <Music2 size={12} /> MY SAVED TRACKS
        </div>
        <button data-testid="track-add-toggle" onClick={() => setAdding(!adding)} className="btn-outline" style={{ padding: "8px 14px", fontSize: 9, display: "flex", alignItems: "center", gap: 6 }}>
          <Plus size={12} /> Log a Track
        </button>
      </div>

      {adding && (
        <div style={{ background: G.panel, border: `1px solid ${G.border}`, padding: 16, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <input data-testid="track-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Track title (e.g. 'Midnight Drill v2')" style={inp} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <select data-testid="track-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="mono" style={{ ...inp, flex: "0 0 120px" }}>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input data-testid="track-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Link to the generated track (optional)" style={{ ...inp, flex: 1, minWidth: 180 }} />
          </div>
          <textarea data-testid="track-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes — which prompt, tweaks you made, what worked…" style={{ ...inp, resize: "vertical", fontFamily: "'Cormorant Garamond',serif" }} />
          <button data-testid="track-save" onClick={save} disabled={saving || !form.title} className="btn-gold" style={{ padding: 11, fontSize: 10 }}>{saving ? "Saving…" : "Save Track"}</button>
        </div>
      )}

      {tracks.length === 0 ? (
        <div style={{ color: G.muted, fontStyle: "italic", fontSize: 14 }}>No tracks logged yet. Generated something good? Log it here to build your production journal.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {tracks.map((t) => (
            <div key={t.id} data-testid={`track-${t.id}`} style={{ borderBottom: `1px solid ${G.border}33`, paddingBottom: 12, display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16, color: "#fff" }}>{t.title}</span>
                  {t.platform && <span className="mono" style={{ fontSize: 8, letterSpacing: ".12em", color: G.gold, border: `1px solid ${G.gold}55`, padding: "2px 7px" }}>{t.platform.toUpperCase()}</span>}
                  {t.link && <a href={t.link} target="_blank" rel="noopener noreferrer" style={{ color: G.goldDim, display: "inline-flex" }}><ExternalLink size={13} /></a>}
                </div>
                {t.notes && <div style={{ fontSize: 13.5, color: G.muted, fontStyle: "italic", marginTop: 4 }}>{t.notes}</div>}
              </div>
              <button data-testid={`track-delete-${t.id}`} onClick={() => remove(t.id)} style={{ background: "none", border: "none", cursor: "pointer", color: G.goldDim, flexShrink: 0 }}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inp = { width: "100%", padding: "10px 12px", background: G.dark, border: `1px solid ${G.border}`, color: G.text, fontSize: 14 };
